import { Component } from '@angular/core'

import { LeaseDetailService } from '../../data/services/view/lease-detail.service'

interface RowKey {
	rowIndex: number
	section: 'income_statement' | 'balance_sheet'
	indent: number
	isCategory: boolean
	isCalculated: boolean
	isCheck: boolean
	label: string
}

/**
 * ADR-0021 — Render dinámico del EEFF Anual.
 *
 * Antes (1610 líneas hardcoded): mapeaba 37 campos fijos del schema (`sales`,
 * `returnsDiscounts`, ...) a celdas Angular específicas.
 *
 * Ahora (~100 líneas): itera `ratiosData.eeffPeriods[i].rows[]` por `rowIndex`.
 * El label viene tal cual del Excel — soporta nombres distintos por cliente
 * (estructura fija, labels dinámicos). Sección + indent dictan el render.
 */
@Component({
	selector: 'app-ratios',
	templateUrl: './ratios.component.html',
	styleUrls: ['./ratios.component.scss'],
})
export class RatiosComponent {
	constructor(public leaseDetailService: LeaseDetailService) {}

	get periods(): any[] {
		const r = this.leaseDetailService.ratiosData as any
		return Array.isArray(r?.eeffPeriods) ? r.eeffPeriods : []
	}

	get variations(): any[] {
		const r = this.leaseDetailService.ratiosData as any
		return Array.isArray(r?.eeffVariations) ? r.eeffVariations : []
	}

	get hasData(): boolean {
		return this.periods.length > 0
	}

	/**
	 * Unión ordenada de filas por `rowIndex` a través de todos los períodos.
	 * Si dos períodos discrepan en label/section/isCategory para un mismo
	 * rowIndex, se conserva el primer descriptor encontrado.
	 */
	get mergedRows(): RowKey[] {
		const map = new Map<number, RowKey>()
		for (const p of this.periods) {
			for (const r of p.rows || []) {
				if (!map.has(r.rowIndex)) {
					map.set(r.rowIndex, {
						rowIndex: r.rowIndex,
						section: r.section,
						indent: r.indent || 0,
						isCategory: !!r.isCategory,
						isCalculated: !!r.isCalculated,
						isCheck: !!r.isCheck,
						label: r.label,
					})
				}
			}
		}
		return Array.from(map.values()).sort((a, b) => a.rowIndex - b.rowIndex)
	}

	get incomeRows(): RowKey[] {
		return this.mergedRows.filter((r) => r.section === 'income_statement')
	}

	get balanceRows(): RowKey[] {
		return this.mergedRows.filter((r) => r.section === 'balance_sheet')
	}

	getAmount(period: any, rowIndex: number): number | null {
		const row = period.rows?.find((r: any) => r.rowIndex === rowIndex)
		return row?.amount ?? null
	}

	getPercentage(period: any, rowIndex: number): number | null {
		const row = period.rows?.find((r: any) => r.rowIndex === rowIndex)
		return row?.percentage ?? null
	}

	getVariation(variation: any, rowIndex: number): number | null {
		const row = variation.rows?.find((r: any) => r.rowIndex === rowIndex)
		return row?.variation ?? null
	}

	/**
	 * Devuelve el resultado del "Check" del balance para un período: 'ok' | 'error' | null.
	 * Normaliza el string crudo del Excel (puede venir como "Ok", "OK", "Error") a un
	 * literal lowercase para que el template no dependa de la capitalización exacta.
	 */
	getCheckStatus(period: any, rowIndex: number): 'ok' | 'error' | null {
		const row = period.rows?.find((r: any) => r.rowIndex === rowIndex)
		const raw: string | null = row?.checkValue ?? null
		if (!raw) return null
		const norm = raw.trim().toLowerCase()
		if (norm === 'ok') return 'ok'
		return 'error'
	}

	trackByRowIndex(_idx: number, r: RowKey): number {
		return r.rowIndex
	}

	trackByPeriod(_idx: number, p: any): number {
		return p.year
	}
}
